// Google Places — exactly the same pattern as the legacy CRM
// (src/Template/Bornes/mapfullscreen.ctp + webroot/js/Bornes/cart_map.js).
//
// We attach `google.maps.places.SearchBox` directly to a regular <input>.
// Google renders its own native suggestions dropdown; on selection, the
// `places_changed` event fires with the chosen place (incl. geometry).
//
// API key resolution order :
//   1. process.env.REACT_APP_GOOGLE_PLACES_API_KEY (Coolify env, build-time)
//   2. process.env.REACT_APP_GOOGLE_API_KEY        (alternate name, build-time)
//   3. sinon désactivé — AUCUNE clé en dur (la clé doit provenir de l'env de build ;
//      une clé committée est exposée dans le bundle et exploitable).

let _loadPromise = null;

function getApiKey() {
  return (
    process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
    process.env.REACT_APP_GOOGLE_API_KEY ||
    ''
  );
}

export function isPlacesEnabled() { return !!getApiKey(); }

export function getApiKeyHint() {
  const k = getApiKey();
  if (!k) return 'no key';
  const fromEnv = !!(process.env.REACT_APP_GOOGLE_PLACES_API_KEY || process.env.REACT_APP_GOOGLE_API_KEY);
  return `${fromEnv ? 'env' : 'fallback'} key=${k.slice(0, 10)}…`;
}

function loadScriptOnce() {
  if (typeof window === 'undefined') return Promise.reject(new Error('SSR'));
  if (window.google?.maps?.places?.SearchBox) return Promise.resolve();
  if (_loadPromise) return _loadPromise;
  const apiKey = getApiKey();
  if (!apiKey) return Promise.reject(new Error('Google Places API key missing'));

  // Surface invalid-key / API-not-enabled errors so the dev sees why.
  window.gm_authFailure = () => {
    console.error('[googlePlaces] Google Maps auth failure — vérifier que la clé est valide, que "Maps JavaScript API" + "Places API" sont activées, et que la restriction "HTTP referrers" autorise ce domaine.');
  };

  _loadPromise = new Promise((resolve, reject) => {
    // Same script URL pattern as CRM PROD (with the v=weekly hint).
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
    s.async = true;
    s.defer = true;
    s.onload = () => {
      if (window.google?.maps?.places?.SearchBox) {
        console.info('[googlePlaces] SDK loaded ok');
        resolve();
      } else {
        console.error('[googlePlaces] SDK loaded but google.maps.places.SearchBox missing');
        reject(new Error('places library missing'));
      }
    };
    s.onerror = (e) => { console.error('[googlePlaces] script load failed', e); reject(e); };
    document.head.appendChild(s);
  });
  return _loadPromise;
}

/**
 * Attach a Google SearchBox to an <input> element. Calls `onPlace({ lat, lng,
 * name, address, bounds })` whenever the user picks a suggestion. Returns a cleanup
 * function that removes the attached listener (the SDK clears the DOM dropdown
 * automatically when the input is unmounted).
 */
export async function attachSearchBox(inputEl, onPlace) {
  if (!inputEl) return () => {};
  try {
    await loadScriptOnce();
  } catch (e) {
    return () => {};
  }
  if (!window.google?.maps?.places?.SearchBox) return () => {};

  const searchBox = new window.google.maps.places.SearchBox(inputEl);
  const listener = searchBox.addListener('places_changed', () => {
    const places = searchBox.getPlaces();
    if (!places || places.length === 0) return;
    const place = places[0];
    if (!place.geometry?.location) return;
    // L'etendue du lieu, quand Google la donne : une ville et un numero de rue
    // ne se cadrent pas au meme zoom. Sans elle, on choisit un zoom d'adresse.
    const vp = place.geometry.viewport;
    onPlace({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
      name: place.name || '',
      address: place.formatted_address || '',
      bounds: vp
        ? [
            [vp.getSouthWest().lat(), vp.getSouthWest().lng()],
            [vp.getNorthEast().lat(), vp.getNorthEast().lng()],
          ]
        : null,
    });
  });
  return () => {
    try { window.google.maps.event.removeListener(listener); } catch {}
    // Remove pac-container (Google's autocomplete dropdown) injected to body
    // for this input — best-effort cleanup, harmless if already gone.
  };
}
